import { useEffect, useMemo, useState } from "react";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { CATEGORY_SLUGS } from "@shared/constants/categories";
import { BENEFICIARIES_SLUG } from "@shared/essentials-spec";
import { FIELD_KEYS } from "@shared/constants/fields";
import { screenByKey } from "@shared/interview/chapters";
import { boardCell, boardOptions, boardPickNotes, boardStep, boardTable, boardTiles, carryFromKid, cellOpen, cellRuledOff, boardWord } from "@shared/interview/board";
import { composeContactName } from "@shared/contact";
import { CHILD_BENEFICIARY_TILE_LABEL, childBeneficiaryWho, isChildBeneficiaryRow } from "@shared/beneficiary-child";
import { useSectionRows, type SectionRow } from "./useSectionRows";
import { saveInBackground } from "./saveFailure";
import { useChildRows } from "./ChildrenScreen";
import { TilePicker, type TileSelection } from "./TilePicker";
import { InterviewRowTable, type RowTableRow } from "./InterviewRowTable";
import { ContactCard, LinkedPersonCell, cardSummary, type CardSlot } from "./PersonPicker";
import { ConsequencePicker, ConsequenceNote, SharedRowNotes, consequenceFor, groupRowNotes } from "./ConsequencePicker";
import { OnePanelProvider, usePanels } from "./useOnePanelOpen";
import { DateCell, NoteCard, OffCell, ReadCell } from "./InterviewCells";
import { FullTextPanel } from "./FullText";

/**
 * s223 — BENEFICIARIES (SPEC: `Main.dc.html`, the `benef` step; D-222.10 part 4).
 *
 * Four tiles — Family · a friend · a charity or organisation · someone else — and "No one". One row per
 * beneficiary in `beneficiaries`.
 *
 * ## What is stored, and why
 *  • 🔴 `beneficiary_minor` is ASKED, never derived from the date of birth: the board asks the yes/no FIRST and
 *    the date only when the answer is yes, so a "no" leaves nothing to calculate from;
 *  • `beneficiary_tile` records which tile the row came from, so a reload can tell which table it belongs in;
 *    a row typed on the Beneficiaries page before that field existed is classified from what it does carry;
 *  • the four unmatched relationship choices are now stored choices (appended to the spec, D-222.10 part 4).
 *
 * ## The two mechanisms, built as SHARED pieces (G7) — later chapters reuse both
 *  • `fromKid` — the planner's own children, carried in from chapter one: they appear in the contact card's
 *    Existing-contact Recent row, and picking one fills the row and shows the carried answers READ-ONLY
 *    (`carryFromKid`, `shared/interview/board.ts`; what each cell carries is declared in `board-map.ts`);
 *  • `onWhen` / `vIn` — a cell shown only once another answer calls for it: the date of birth appears when
 *    "Under 18?" is Yes (`cellOpen`, same module). Neither is beneficiary-specific.
 *
 * ## Both halves of the board's remaining vocabulary, built at s223
 *  • 🔴 `rules` (D-223.18) — a charity row is asked neither "Under 18?" nor a date of birth, and rule 45 says
 *    what it draws instead: **"not asked" in light grey** (the board's words since v331), never hidden and never an empty box. A date
 *    already typed is KEPT (switching the row back finds it), and while the row is a charity that value counts
 *    as ABSENT EVERYWHERE — the documents read it through `shared/lib/row-gates`, so it cannot reach form 74A,
 *    which reads a beneficiary's date of birth (rule 85).
 *  • 🔴 `typed` (D-223.19) — the "Someone else" tile takes a TYPED LEAD in the row's first column, where a
 *    tile's name would sit, stored in `beneficiary_who_they_are`. Without it the executor is handed a
 *    beneficiary whose only identity is the words "Someone else". A note was rejected: nothing reads a note.
 *
 * Deselect / "No one" / × HIDE rows (isHidden, no value) and never delete (G8).
 *
 * ## s234 — the children are rows of this table (CH1-CHILDREN-ON-BENEFICIARIES; D-233.72, D-234.43, D-234.47)
 *  • every child on Children is a row here, FIRST, in Children's order, drawn as the board declares it: Which kind
 *    "Your child", the name, "My child" (or the relationship itself for a ward, a stepchild, a grandchild), Under 18?,
 *    date of birth. The row is the child's own person, linked (`shared/beneficiary-child.ts`): the name and the date
 *    of birth are the SAME answers as on Children, so editing either here changes it there. The server makes, hides
 *    and removes the row with the child; this screen never creates, hides or removes one — no Add row and no ×;
 *  • the relationship is drawn, never written (the server refuses it too);
 *  • the tiles, "No one" and the tile rows below never touch a child's row;
 *  • 🔴 the old `fromKid` COPY (picking a child in the card wrote a second person) is retired — a row carried that way
 *    before s234 still shows its answers read-only (`isFromKid`).
 */

const BENEF = boardStep("benef");
const BENEF_TILES = boardTiles(BENEF);
// `typed` and `full` ride along: the "Someone else" tile draws a typed lead instead of its name (D-223.19).
const TILES = BENEF_TILES.tiles.map(({ id, label, sub, mark, color, typed, full }) => ({ id, label, sub, mark, color, typed, full }));
/** Tile id → the stored "which kind": the tile's own words. */
export const TILE_VALUE: Readonly<Record<string, string>> = Object.fromEntries(BENEF_TILES.tiles.map((t) => [t.id, t.label]));
const TILE_OF_VALUE = new Map(Object.entries(TILE_VALUE).map(([id, v]) => [v, id]));
const CHARITY_TILE = "charity";

const CELL = { who: boardCell(BENEF, "who"), rel: boardCell(BENEF, "rel"), minor: boardCell(BENEF, "minor"), dob: boardCell(BENEF, "dob") };
/**
 * s227 (D14) — the hover title on a cut read-only answer. The board draws no reveal on a read cell, so these are the
 * product's words (the "Full name" pair is the board's, for names only) — named for the G4.1 copy sweep.
 */
export const FULL_ANSWER_TITLE = "Show the full answer";
export const BENEF_RELATIONSHIPS = boardOptions("benef", CELL.rel);
export const MINOR_OPTIONS = boardOptions("benef", CELL.minor);
/** s236 (rule 158, D-235.105(2), D-236.87): a child's who-they-are on Beneficiaries — Children's OWN choices and stored values
 *  (the one person's relationship). A held answer that is not one of them (an older "Ward", "Child") stays shown (G8). */
export const CHILD_REL_OPTIONS = boardOptions("kids", boardCell(boardStep("kids"), "crel"));
export function childRelOptions(held: string): Array<{ value: string; label: string }> {
  return withHeld(CHILD_REL_OPTIONS, held, childBeneficiaryWho(held));
}
/** A held answer that is not one of a picker's options stays shown, in its own words (G8). Pure. */
export function withHeld(options: readonly (string | { value: string; label: string })[], held: string, label: string = held): Array<{ value: string; label: string }> {
  const base = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  return held && !base.some((o) => o.value === held) ? [...base, { value: held, label }] : base;
}
const MINOR_NOTES = boardPickNotes("benef", "minor");
export const BENEF_TABLE = boardTable(BENEF);
const NOTE_FOR = BENEF.noteFor ?? {};

/** The stored `beneficiary_type` a tile implies — the existing field every reader already uses. */
const TYPE_FOR_TILE = (tileId: string) => (tileId === CHARITY_TILE ? "Charity" : "Person");
/** Relationships that read as a charity or an organisation, for a row typed before the tile was stored. */
const CHARITY_RELATIONSHIP = "Charity / Organization";
/** Relationships that read as a friend rather than family, same case. */
const FRIEND_RELATIONSHIPS = ["Friend", "Neighbour", "Colleague", "Godchild"];

/**
 * Which tile a row belongs to. The stored tile wins; a row typed on the plan's own page before that field existed
 * is classified from its type and relationship, and falls back to the "someone else" tile rather than vanishing.
 * Pure.
 */
export function tileOfBeneficiary(values: Readonly<Record<string, string>>): string {
  const stored = TILE_OF_VALUE.get(values[FIELD_KEYS.BENEFICIARY_TILE] ?? "");
  if (stored) return stored;
  const type = values[FIELD_KEYS.BENEFICIARY_TYPE] ?? "";
  const rel = values[FIELD_KEYS.BENEFICIARY_RELATIONSHIP] ?? "";
  if (type === "Charity" || type === "Organization" || rel === CHARITY_RELATIONSHIP) return CHARITY_TILE;
  if (FRIEND_RELATIONSHIPS.includes(rel)) return "friend";
  if (rel === "" || rel === "Other") return "other";
  return "family";
}

/** A row names a beneficiary once it carries a name (a charity's whole name lives in the surname). Pure. */
export function hasBeneficiaryName(values: Readonly<Record<string, string>>): boolean {
  return composeContactName({ firstName: values[FIELD_KEYS.BENEFICIARY_FIRST_NAME], surname: values[FIELD_KEYS.BENEFICIARY_SURNAME] }) !== "";
}

/** 🔴 The row was carried in from chapter one: its relationship is the one `fromKid` stores. Pure. */
export function isFromKid(values: Readonly<Record<string, string>>): boolean {
  const carried = carryFromKid("benef", CELL.rel, { dob: "" }, "1970-01-01");
  return !!carried && values[FIELD_KEYS.BENEFICIARY_RELATIONSHIP] === carried.value;
}

/**
 * s234 — the child's row, as board 1 v443 draws it (`Main.dc.html`, the `benef` step: `mark: 'CHILD'`,
 * `markBg: '#E08A2E'`). The row's "which kind" is `CHILD_BENEFICIARY_TILE_LABEL` ("Your child").
 */
const CHILD_MARK = { mark: "CHILD", color: "#E08A2E" };
/** A child's card asks only the name — no contact details are asked on a child's row (D-233.72). */
const CHILD_SLOTS: CardSlot[] = [{
  label: "Person 1",
  name: { kind: "nameParts", first: FIELD_KEYS.BENEFICIARY_FIRST_NAME, last: FIELD_KEYS.BENEFICIARY_SURNAME },
}];

/** The children's rows, first, in Children's order (the child rows' own order on that screen); a child's row the
 *  Children list does not name yet keeps its place after them. Pure. */
export function orderChildRows<R extends { rowId: string }>(childRows: readonly R[], childrenOrder: readonly string[]): R[] {
  const at = new Map(childrenOrder.map((id, i) => [id, i]));
  return childRows.map((r, i) => ({ r, k: at.get(r.rowId) ?? childrenOrder.length + i })).sort((a, b) => a.k - b.k).map((x) => x.r);
}

/** The card's slots: a person's name parts — or, for a charity, the COMPANY card (s236, D-236.29(a); rule 150): its whole
 *  name in the surname ("or organization name", the s156 rule), Main phone, Email or website, then "+ Add a person to ask
 *  for" (D-236.40(2)). The school card's shape (DayToDayScreen). */
export const slotsFor = (charity: boolean): CardSlot[] => charity
  ? [
      { label: "The company", org: true, name: { kind: "text", key: FIELD_KEYS.BENEFICIARY_SURNAME },
        phone: FIELD_KEYS.BENEFICIARY_PHONE, email: FIELD_KEYS.BENEFICIARY_EMAIL, address: FIELD_KEYS.BENEFICIARY_ADDRESS },
      { label: "A person to ask for", name: { kind: "contact", key: FIELD_KEYS.BENEFICIARY_ASK_FOR },
        phone: FIELD_KEYS.BENEFICIARY_ASK_FOR_PHONE, email: FIELD_KEYS.BENEFICIARY_ASK_FOR_EMAIL, address: FIELD_KEYS.BENEFICIARY_ASK_FOR_ADDRESS },
    ]
  : [{
      label: "Person 1",
      name: { kind: "nameParts", first: FIELD_KEYS.BENEFICIARY_FIRST_NAME, last: FIELD_KEYS.BENEFICIARY_SURNAME },
      phone: FIELD_KEYS.BENEFICIARY_PHONE, email: FIELD_KEYS.BENEFICIARY_EMAIL, address: FIELD_KEYS.BENEFICIARY_ADDRESS,
    }];

export function BenefScreen({ clientId, onAnswered }: { clientId: string; onAnswered: () => void }) {
  return (
    <OnePanelProvider>
      <BenefScreenBody clientId={clientId} onAnswered={onAnswered} />
    </OnePanelProvider>
  );
}

function BenefScreenBody({ clientId, onAnswered }: { clientId: string; onAnswered: () => void }) {
  const today = new Date().toISOString().slice(0, 10); // D-234.81: the date a child's Under 18? is worked out on
  const data = useSectionRows(clientId, CATEGORY_SLUGS.LEGAL, BENEFICIARIES_SLUG, onAnswered);
  const { rows, fieldIdByKey } = data;
  const panels = usePanels();
  const createFlight = useSingleFlight();
  const [createdHere, setCreatedHere] = useState<ReadonlySet<string>>(() => new Set());
  const create = (tileId: string, extra: Record<string, string> = {}) =>
    createFlight.run(() => data.createRow({
      [FIELD_KEYS.BENEFICIARY_TILE]: TILE_VALUE[tileId],
      [FIELD_KEYS.BENEFICIARY_TYPE]: TYPE_FOR_TILE(tileId),
      ...extra,
    })).then((id) => { if (id) setCreatedHere((s) => new Set(s).add(id)); });

  // s234: the children's own rows — linked to the child, never copied — come first, in Children's order. The order is
  // read through the children screens' own hook (G7); this screen writes nothing there.
  const kidsData = useChildRows(clientId, () => {});
  const childrenOrder = useMemo(() => kidsData.rows.filter((r) => r.kind === "child").map((r) => r.rowId), [kidsData.rows]);
  const childRows = orderChildRows(rows.filter((r) => !r.hidden && isChildBeneficiaryRow(r.values)), childrenOrder);
  // Every tile, "No one", Add row and × below work on the planner's own rows only — never on a child's.
  const tileRows = rows.filter((r) => !isChildBeneficiaryRow(r.values));

  const visible = tileRows.filter((r) => !r.hidden);
  const [selection, setSelection] = useState<TileSelection | null>(null);
  useEffect(() => {
    if (selection || !data.ready) return;
    const selected = TILES.map((t) => t.id).filter((id) => visible.some((r) => hasBeneficiaryName(r.values) && tileOfBeneficiary(r.values) === id));
    setSelection({ selected, none: false });
  }, [selection, data.ready, visible]);
  const sel = selection ?? { selected: [], none: false };

  const turnOn = async (tileId: string) => {
    const ofTile = tileRows.filter((r) => tileOfBeneficiary(r.values) === tileId);
    if (ofTile.some((r) => r.hidden)) {
      for (const r of ofTile.filter((x) => x.hidden)) await data.setRowHidden(r.rowId, false);
      data.refresh();
    } else if (ofTile.length === 0) {
      await create(tileId);
    } else if (!ofTile.some((r) => hasBeneficiaryName(r.values))) {
      setCreatedHere((s) => new Set(s).add(ofTile[ofTile.length - 1].rowId));
    }
  };
  const turnOff = async (tileId: string) => {
    for (const r of tileRows.filter((x) => tileOfBeneficiary(x.values) === tileId && !x.hidden)) await data.setRowHidden(r.rowId, true);
    data.refresh();
  };

  const onTiles = async (next: TileSelection, changed: string) => {
    setSelection(next);
    if (changed === "none") {
      if (!next.none) return;
      for (const r of visible) await data.setRowHidden(r.rowId, true);
      data.refresh();
      return;
    }
    if (next.selected.includes(changed)) await turnOn(changed);
    else await turnOff(changed);
  };

  const [removedHere, setRemovedHere] = useState<ReadonlyMap<string, string>>(() => new Map());
  const removeRow = async (rowId: string) => {
    const r = rows.find((x) => x.rowId === rowId);
    const name = r ? composeContactName({ firstName: r.values[FIELD_KEYS.BENEFICIARY_FIRST_NAME], surname: r.values[FIELD_KEYS.BENEFICIARY_SURNAME] }) : "";
    setRemovedHere((m) => new Map(m).set(rowId, name || "This person"));
    try {
      await data.setRowHidden(rowId, true);
    } catch (err) {
      // s228 (ruling 4): a refused × puts the row back — never "… removed · Undo" for a removal that did not happen.
      setRemovedHere((m) => { const n = new Map(m); n.delete(rowId); return n; });
      throw err;
    }
    data.refresh();
  };
  const undoRemove = async (rowId: string) => {
    await data.setRowHidden(rowId, false);
    setRemovedHere((m) => { const n = new Map(m); n.delete(rowId); return n; });
    data.refresh();
  };

  const drawn = tileRows.filter((r) => {
    if (removedHere.has(r.rowId)) return true;
    if (r.hidden) return false;
    return sel.selected.includes(tileOfBeneficiary(r.values)) && (hasBeneficiaryName(r.values) || createdHere.has(r.rowId));
  });

  // D-234.81: a child's Under 18? worked out from the date of birth (the board's carry rule), else the row's own answer.
  const childMinor = (v: Readonly<Record<string, string>>): { worked: { value: string; word: string } | null; value: string } => {
    const dob = v[FIELD_KEYS.BENEFICIARY_DOB] ?? "";
    const worked = dob ? carryFromKid("benef", CELL.minor, { dob }, today) : null;
    return { worked, value: worked?.value ?? v[FIELD_KEYS.BENEFICIARY_MINOR] ?? "" };
  };
  // s234 (D-234.94): the under-18 note that is IDENTICAL on several rows shows once under the table, naming them.
  const nameOf = (v: Readonly<Record<string, string>>) =>
    (v[FIELD_KEYS.BENEFICIARY_FIRST_NAME] ?? "").trim() || composeContactName({ firstName: v[FIELD_KEYS.BENEFICIARY_FIRST_NAME], surname: v[FIELD_KEYS.BENEFICIARY_SURNAME] }) || "This person";
  const minorNotes = groupRowNotes([
    ...childRows.map((r) => ({ rowId: r.rowId, name: nameOf(r.values), text: consequenceFor(MINOR_NOTES, childMinor(r.values).value) })),
    ...drawn.filter((r) => !removedHere.has(r.rowId)).map((r) => ({ rowId: r.rowId, name: nameOf(r.values), text: consequenceFor(MINOR_NOTES, r.values[FIELD_KEYS.BENEFICIARY_MINOR] ?? "") })),
  ]);
  const sharedNoteRows = new Set(minorNotes.shared.flatMap((n) => n.rowIds));

  const table: RowTableRow[] = drawn.map((r: SectionRow) => {
    const v = r.values;
    const tileId = tileOfBeneficiary(v);
    const tile = TILES.find((t) => t.id === tileId);
    const lead = tile?.label ?? "";
    if (removedHere.has(r.rowId)) {
      return { rowId: r.rowId, lead, cells: [], removed: { label: removedHere.get(r.rowId)!, onUndo: () => saveInBackground(undoRemove(r.rowId)) } };
    }
    const tid = (what: string) => `interview-benef-${what}-${r.rowId}`;
    const whoKey = `${r.rowId}:who`;
    const noteKey = `${r.rowId}:note`;
    const charity = tileId === CHARITY_TILE;
    // s236 (D-236.29(a)): the board's per-tile company flag (`orgBy` on the who cell) opens the company card.
    const company = !!CELL.who.orgBy?.[tileId];
    const slots = slotsFor(company);
    const headerOf = (k: string) => BENEF_TABLE.columns.find((c) => c.key === k)?.label ?? "";
    // s227 (D14): a read-only answer that is cut opens in full under the row, labelled with its own column header.
    const fullKey = (k: string) => `${r.rowId}:${k}:full`;
    const reveal = (k: string) => ({ open: panels.isOpen(fullKey(k)), onToggle: () => panels.toggle(fullKey(k)), title: FULL_ANSWER_TITLE });
    const readPanels = (["rel", "minor", "dob"] as const).map((k) => ({ k, text: k === "rel" ? (CELL.rel.fromKid ?? "")
      : k === "minor" ? (boardWord("benef", "minor", v[FIELD_KEYS.BENEFICIARY_MINOR]) ?? "") : (v[FIELD_KEYS.BENEFICIARY_DOB] ?? "") }));
    // s236 (D-235.105(4)): a "Child" row with no Children link (Alex Taylor, the Avery case) is an ORDINARY beneficiary —
    // every answer editable; nothing is read-only on it any more.
    const carried = false;
    // s223 (D-223.18): the board's per-tile `rules` — a charity row does not ask these two.
    const ruledOff = (k: string) => cellRuledOff(BENEF, tileId, k);
    // The board's onWhen: the date of birth is asked only once "Under 18?" is Yes.
    const dobOpen = cellOpen("benef", CELL.dob, (k) => (k === "minor" ? v[FIELD_KEYS.BENEFICIARY_MINOR] : undefined));

    const relCell = carried && v[FIELD_KEYS.BENEFICIARY_RELATIONSHIP]
      ? <ReadCell key="rel" text={CELL.rel.fromKid ?? ""} testId={tid("rel")} reveal={reveal("rel")} />
      : <ConsequencePicker key="rel" value={v[FIELD_KEYS.BENEFICIARY_RELATIONSHIP] ?? ""} options={withHeld(BENEF_RELATIONSHIPS, v[FIELD_KEYS.BENEFICIARY_RELATIONSHIP] ?? "", childBeneficiaryWho(v[FIELD_KEYS.BENEFICIARY_RELATIONSHIP]))} placeholder={CELL.rel.ph}
          ariaLabel={headerOf("rel")} testId={tid("rel")} onCommit={(val) => saveInBackground(data.writeCells(r.rowId, { [FIELD_KEYS.BENEFICIARY_RELATIONSHIP]: val }))} />;
    const minorCell = ruledOff("minor")
      ? <OffCell key="minor" testId={tid("minor-off")} />
      : carried && v[FIELD_KEYS.BENEFICIARY_MINOR]
      ? <ReadCell key="minor" text={boardWord("benef", "minor", v[FIELD_KEYS.BENEFICIARY_MINOR]) ?? ""} testId={tid("minor")} reveal={reveal("minor")} />
      : <ConsequencePicker key="minor" value={v[FIELD_KEYS.BENEFICIARY_MINOR] ?? ""} options={MINOR_OPTIONS} placeholder={CELL.minor.ph}
          ariaLabel={headerOf("minor")} testId={tid("minor")} onCommit={(val) => saveInBackground(data.writeCells(r.rowId, { [FIELD_KEYS.BENEFICIARY_MINOR]: val }))} />;
    const dobCell = ruledOff("dob") || !dobOpen
      ? <OffCell key="dob" testId={tid("dob-off")} />
      : carried && v[FIELD_KEYS.BENEFICIARY_DOB]
        ? <ReadCell key="dob" text={v[FIELD_KEYS.BENEFICIARY_DOB]} testId={tid("dob")} reveal={reveal("dob")} />
        : <DateCell key="dob" value={v[FIELD_KEYS.BENEFICIARY_DOB] ?? ""} ariaLabel={headerOf("dob")} testId={tid("dob")}
            onCommit={(d) => saveInBackground(data.writeCells(r.rowId, { [FIELD_KEYS.BENEFICIARY_DOB]: d }))} />;

    return {
      rowId: r.rowId,
      lead,
      ...(tile?.mark && tile.color ? { leadMark: { mark: tile.mark, color: tile.color } } : {}),
      // s223 (D-223.19): a `typed` tile's lead is a box, with the tile's own `full` as its placeholder.
      ...(tile?.typed
        ? {
            leadInput: {
              value: v[FIELD_KEYS.BENEFICIARY_WHO_THEY_ARE] ?? "",
              placeholder: tile.full,
              onCommit: (val: string) => saveInBackground(data.writeCells(r.rowId, { [FIELD_KEYS.BENEFICIARY_WHO_THEY_ARE]: val })),
              testId: tid("who-they-are"),
            },
          }
        : {}),
      cells: [
        <LinkedPersonCell key="who" value="" label={cardSummary(slots.slice(0, 1), v)} word={CELL.who.word ?? ""} org={company}
          open={panels.isOpen(whoKey)} onOpen={() => panels.toggle(whoKey)} testId={tid("who")} />,
        relCell, minorCell, dobCell,
      ],
      canAddRow: true,
      note: {
        value: v[FIELD_KEYS.NOTES] ?? "",
        open: panels.isOpen(noteKey),
        onToggle: () => panels.toggle(noteKey),
        // s237 (D-237.30): the save is returned, so the Note's Done waits for it.
        onCommit: (val) => (val !== (v[FIELD_KEYS.NOTES] ?? "") ? data.writeCells(r.rowId, { [FIELD_KEYS.NOTES]: val }) : undefined),
      },
      panels: (
        <>
          {panels.isOpen(whoKey) && (
            <ContactCard interviewLook key={whoKey} title={CELL.who.title ?? ""} slots={slots} values={v} fieldIdByKey={fieldIdByKey} clientId={clientId}
              undo={CELL.who.undo} {...(company ? { moreLabel: "+ Add a person to ask for" } : {})} onSave={(c) => data.writeCells(r.rowId, c)} onClose={() => panels.close(whoKey)} testId={tid("card")} />
          )}
          {readPanels.filter((p) => panels.isOpen(fullKey(p.k))).map((p) => (
            <FullTextPanel key={p.k} label={`${headerOf(p.k)}:`} text={p.text} testId={`${tid(p.k)}-text`} />
          ))}
          {!sharedNoteRows.has(r.rowId) && <ConsequenceNote notes={MINOR_NOTES} value={v[FIELD_KEYS.BENEFICIARY_MINOR] ?? ""} testId={tid("minor-note")} />}
        </>
      ),
    };
  });

  // s234 (D-233.72, D-234.47): the children's rows, as the board draws them — first, no ×, no Add row. The name and
  // the date of birth write the child's own answers (the server routes them to the child's person); who they are is
  // drawn from the child's relationship and never written; Under 18? is worked out from the date of birth (D-234.81)
  // and asked only when there is none.
  const childTable: RowTableRow[] = childRows.map((r: SectionRow) => {
    const v = r.values;
    const tid = (what: string) => `interview-benef-${what}-${r.rowId}`;
    const whoKey = `${r.rowId}:who`;
    const noteKey = `${r.rowId}:note`;
    const headerOf = (k: string) => BENEF_TABLE.columns.find((c) => c.key === k)?.label ?? "";
    // D-234.81: with a date of birth, Under 18? is worked out from it (the board's own carry rule) — read-only and never
    // stored, so it cannot go stale on an 18th birthday; it is asked only when there is no date of birth.
    const { worked, value: minorValue } = childMinor(v);
    return {
      rowId: r.rowId,
      lead: CHILD_BENEFICIARY_TILE_LABEL,
      leadMark: CHILD_MARK,
      cells: [
        <LinkedPersonCell key="who" value="" label={cardSummary(CHILD_SLOTS, v)} word={CELL.who.word ?? ""}
          open={panels.isOpen(whoKey)} onOpen={() => panels.toggle(whoKey)} testId={tid("who")} />,
        // s236 (rule 158, D-235.105(2)): who they are is edited here too — the child's own person's relationship, one record.
        <ConsequencePicker key="rel" value={v[FIELD_KEYS.BENEFICIARY_RELATIONSHIP] ?? ""} options={childRelOptions(v[FIELD_KEYS.BENEFICIARY_RELATIONSHIP] ?? "")}
          placeholder={CELL.rel.ph} ariaLabel={headerOf("rel")} testId={tid("rel")}
          onCommit={(val) => saveInBackground(data.writeCells(r.rowId, { [FIELD_KEYS.BENEFICIARY_RELATIONSHIP]: val }))} />,
        worked
          ? <ReadCell key="minor" text={worked.word} testId={tid("minor")} />
          : <ConsequencePicker key="minor" value={v[FIELD_KEYS.BENEFICIARY_MINOR] ?? ""} options={MINOR_OPTIONS} placeholder={CELL.minor.ph}
              ariaLabel={headerOf("minor")} testId={tid("minor")} onCommit={(val) => saveInBackground(data.writeCells(r.rowId, { [FIELD_KEYS.BENEFICIARY_MINOR]: val }))} />,
        <DateCell key="dob" value={v[FIELD_KEYS.BENEFICIARY_DOB] ?? ""} ariaLabel={headerOf("dob")} testId={tid("dob")}
          onCommit={(d) => saveInBackground(data.writeCells(r.rowId, { [FIELD_KEYS.BENEFICIARY_DOB]: d }))} />,
      ],
      noRemove: true,
      canAddRow: false,
      note: {
        value: v[FIELD_KEYS.NOTES] ?? "",
        open: panels.isOpen(noteKey),
        onToggle: () => panels.toggle(noteKey),
        // s237 (D-237.30): the save is returned, so the Note's Done waits for it.
        onCommit: (val) => (val !== (v[FIELD_KEYS.NOTES] ?? "") ? data.writeCells(r.rowId, { [FIELD_KEYS.NOTES]: val }) : undefined),
      },
      panels: (
        <>
          {panels.isOpen(whoKey) && (
            <ContactCard interviewLook key={whoKey} title={CELL.who.title ?? ""} slots={CHILD_SLOTS} values={v} fieldIdByKey={fieldIdByKey} clientId={clientId}
              undo={CELL.who.undo} linkedPerson onSave={(c) => data.writeCells(r.rowId, c)} onClose={() => panels.close(whoKey)} testId={tid("card")} />
          )}
          {!sharedNoteRows.has(r.rowId) && <ConsequenceNote notes={MINOR_NOTES} value={minorValue} testId={tid("minor-note")} />}
        </>
      ),
    };
  });

  if (data.ready && !(fieldIdByKey.has(FIELD_KEYS.BENEFICIARY_MINOR) && fieldIdByKey.has(FIELD_KEYS.BENEFICIARY_TILE))) {
    return (
      <p className="text-[14px] text-destructive" data-testid="interview-benef-not-ready">
        This part of your plan isn't ready yet. Please try again shortly.
      </p>
    );
  }

  return (
    <div data-testid="interview-benef">
      <TilePicker compact tiles={TILES} selection={sel} onChange={(n, c) => saveInBackground(onTiles(n, c))} none={screenByKey("benef")?.none}
        columns={BENEF_TILES.cols as 2 | 3 | 4 | 5} testIdPrefix="interview-benef" />
      <InterviewRowTable rowMarks strongRowLine {...BENEF_TABLE} rows={[...childTable, ...table]}
        onAddRow={(rowId) => { const r = rows.find((x) => x.rowId === rowId); if (r) saveInBackground(create(tileOfBeneficiary(r.values))); }}
        onRemove={(id) => saveInBackground(removeRow(id))} testIdPrefix="interview-benef-table" cutLead />
      <SharedRowNotes notes={minorNotes.shared} testIdPrefix="interview-benef" />
      {/* The board places each picked tile's note AFTER the table, as a tinted card with its mark. */}
      <div className="mt-6 flex flex-col gap-1.5">
        {TILES.filter((t) => sel.selected.includes(t.id) && NOTE_FOR[t.id]).map((t) => (
          <NoteCard key={t.id} mark={t.mark ?? ""} color={t.color ?? ""} testId={`interview-benef-note-${t.id}`}>{NOTE_FOR[t.id]}</NoteCard>
        ))}
      </div>
    </div>
  );
}
